import { useAcsFetch } from '../hooks/use-acs-fetch'
import {
  AccountDetails,
  AddBenePayload,
  AddIraBenePayloadObject,
  CreateNonIraBeneResponse,
  DeleteNonIraBenePayload,
  EditNonIraBeneResponse,
  EditPayload,
  FetchUniqueBeneResponse,
} from '../types'

export interface UpdateOptions {
  accountNickname?: string
}

// TO DO- confirm that these types are in line with api
export const useBeneficiariesApi = (): {
  fetchBeneficiaries: () => Promise<AccountDetails[]>
  createBeneficiary: (
    accountId: string,
    payload: AddBenePayload,
  ) => Promise<CreateNonIraBeneResponse>
  createIraBeneficiary: (
    accountId: string,
    payload: AddIraBenePayloadObject | Partial<AddBenePayload>,
  ) => Promise<CreateNonIraBeneResponse>
  updateBeneficiary: (
    accountId: string,
    payload: EditPayload,
  ) => Promise<EditNonIraBeneResponse>
  deleteBeneficiary: (payload: DeleteNonIraBenePayload) => Promise<any>
  fetchUniqueBeneficiaries: (
    id: string,
    isIraPage: boolean,
  ) => Promise<FetchUniqueBeneResponse>
  updateIraBeneficiary: (
    accountId: string,
    payload: EditPayload,
  ) => Promise<any>
} => {
  const acsFetch = useAcsFetch({
    withCif: true,
    withGuid: true,
    defaultHeaders: {
      ApplicationVersion: '2.9',
      ApplicationID: 'ALLYUSBOLB',
      ApplicationName: 'AOB',
    },
  })

  const fetchBeneficiaries = async (): Promise<AccountDetails[]> => {
    const result = await acsFetch<Record<string, any>>(
      `/v1/bank-accounts/beneficiary/accounts/beneficiaries`,
    )

    if (result.data.accounts) {
      return result.data.accounts as any
    }

    throw new Error('No beneficiary payload in successful response')
  }

  const fetchUniqueBeneficiaries = async (
    id: string,
    isIraPage: boolean,
  ): Promise<FetchUniqueBeneResponse> => {
    const result = await acsFetch<FetchUniqueBeneResponse>(
      `/v1/bank-accounts/beneficiary/unique/beneficiaries?${
        isIraPage ? 'planId' : 'accountId'
      }=${id}`,
    )

    if (result.data) {
      return result.data as any
    }

    throw new Error('No beneficiary payload in successful response')
  }

  const createBeneficiary = async (
    accountId: string,
    payload: AddBenePayload,
  ): Promise<CreateNonIraBeneResponse> => {
    const result = await acsFetch<Record<string, any>>(
      `/v1/bank-accounts/beneficiary/accounts/${accountId}/beneficiaries`,
      { method: 'POST', body: JSON.stringify(payload) },
    )

    if (result) {
      return result as any
    }

    throw new Error('No beneficiary payload in successful response')
  }

  const createIraBeneficiary = async (
    accountId: string,
    payload: any,
  ): Promise<CreateNonIraBeneResponse> => {
    const result = await acsFetch<Record<string, any>>(
      `/v1/bank-accounts/beneficiary/iraplans/${accountId}/${
        payload && payload.beneficiaries ? 'beneficiaries' : 'beneficiary'
      }`,
      {
        method: payload && payload.beneficiaries ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      },
    )

    if (result.data) {
      return result.data as any
    }

    throw new Error('No beneficiary payload in successful response')
  }

  const updateBeneficiary = async (
    accountId: string,
    payload: EditPayload,
  ): Promise<EditNonIraBeneResponse> => {
    const result = await acsFetch<Record<string, any>>(
      `/v1/bank-accounts/beneficiary/accounts/${accountId}/beneficiaries`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    )
    if (result) {
      return result as any
    }

    throw new Error('No beneficiaries payload in successful response')
  }

  const updateIraBeneficiary = async (
    accountId: string,
    payload: EditPayload,
  ): Promise<any> => {
    const result = await acsFetch<Record<string, any>>(
      `/v1/bank-accounts/beneficiary/iraplans/${accountId}/beneficiaries`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    )
    if (result) {
      return result as any
    }

    throw new Error('No beneficiaries payload in successful response')
  }

  const deleteBeneficiary = async (
    payload: DeleteNonIraBenePayload,
  ): Promise<any> => {
    const result = await acsFetch<Record<string, any>>(
      `/v1/bank-accounts/beneficiary/accounts/${payload.accountId}/beneficiaries`,
      {
        method: 'DELETE',
        body: JSON.stringify({ beneficiaries: payload.beneficiaries }),
      },
    )
    return result
  }

  return {
    fetchBeneficiaries,
    createBeneficiary,
    updateBeneficiary,
    deleteBeneficiary,
    fetchUniqueBeneficiaries,
    createIraBeneficiary,
    updateIraBeneficiary,
  }
}
